import * as React from 'react';
import {StyleSheet, View, ViewStyle} from 'react-native';

import BaseSceneCommandModal from './BaseSceneCommandModal';

import FullScreenIllustrationView from './visualizations/FullScreenIllustrationView';

import DeleteButton from './buttons/DeleteButton';
import ChangeButton from './buttons/ChangeButton';

import UserStatusContext from '../../../../shared/user_status/UserStatusContext';
import AttachmentSoundView from '../../../../shared/sound/AttachmentSoundView';

import {ModalParams} from '../../Modal';

import CompositeSequenceSceneCommandFormFactory from '../../../../../view_models/CompositeSequenceSceneCommandFormFactory';

import SceneCommandForm from '../../../../../../domain/forms/scene_commands/SceneCommandForm';
import FullScreenIllustrationShowSceneCommandForm from '../../../../../../domain/forms/scene_commands/FullScreenIllustrationShowSceneCommandForm';
import Sound from '../../../../../../domain/entities/Sound';

interface Props {
  sceneCommandForm: FullScreenIllustrationShowSceneCommandForm;
  parentSceneCommandForm?: SceneCommandForm | null;
  onRequestCloseModal: () => void;
  onForwardToChangeFullScreenIllustrations: (
    sceneCommandForm: FullScreenIllustrationShowSceneCommandForm,
    parentSceneCommandId?: number,
  ) => void;
  onRemoveFullScreenIllustrationShowCommand: (
    sceneCommandForm: FullScreenIllustrationShowSceneCommandForm,
  ) => void;
  onForwardToAddSoundEffects: (
    sceneCommandForm: FullScreenIllustrationShowSceneCommandForm,
    parentSceneCommandId?: number,
    callback?: (sound: Sound) => void,
  ) => void;
  onChangeFullScreenIllustrationShowSceneCommandForm: (
    sceneCommandForm: FullScreenIllustrationShowSceneCommandForm,
  ) => void;
  onChangeCommand: (sceneCommandForm: SceneCommandForm) => void;
  onRemoveCommand: (sceneCommandForm: SceneCommandForm) => void;
  onRequestUpdateModal: (modalParams: ModalParams) => void;
}

interface State {
  selectedSound: Sound | null;
  selectedSoundStartTime: number | null;
  selectedSoundEndTime: number | null;
}

export default class FullScreenIllustrationShowSceneCommandModal extends React.PureComponent<
  Props,
  State
> {
  constructor(props: Props) {
    super(props);
    this.state = {
      selectedSound: props.sceneCommandForm.sound || null,
      selectedSoundStartTime: props.sceneCommandForm.startTime || null,
      selectedSoundEndTime: props.sceneCommandForm.endTime || null,
    };
  }

  public render(): React.ReactNode {
    const {sceneCommandForm, onRequestCloseModal} = this.props;
    const {selectedSound, selectedSoundStartTime, selectedSoundEndTime} =
      this.state;
    return (
      <BaseSceneCommandModal
        title={'スチルの編集'}
        onRequestCloseModal={onRequestCloseModal}
        footer={
          <View style={styles.footer}>
            <View style={styles.deleteButtons}>
              <View style={styles.button}>
                <DeleteButton onPress={this.destroySceneCommandForm} />
              </View>
            </View>
          </View>
        }>
        <View style={styles.body}>
          <View
            style={{
              height: 28,
              margin: 8,
              width: '100%',
              alignItems: 'flex-end',
            }}>
            <ChangeButton
              title={'スチル変更'}
              onPress={this.changeIllustration}
            />
          </View>
          <FullScreenIllustrationView
            fullScreenIllustration={sceneCommandForm.fullScreenIllustration}
          />
          <UserStatusContext.Consumer>
            {userStatusContext =>
              (userStatusContext.currentUser?.enabledPaidSubscriber ||
                userStatusContext.currentUser?.isGradeBlack()) && (
                <AttachmentSoundView
                  style={{width: '95%', marginVertical: 10}}
                  sound={selectedSound}
                  startTime={selectedSoundStartTime}
                  endTime={selectedSoundEndTime}
                  addSound={this.addSound}
                  deleteSound={this.deleteSound}
                  onChagenRegion={this.handleChagenRegion}
                />
              )
            }
          </UserStatusContext.Consumer>
        </View>
      </BaseSceneCommandModal>
    );
  }

  private changeIllustration = () => {
    const {
      sceneCommandForm,
      parentSceneCommandForm,
      onRequestCloseModal,
      onForwardToChangeFullScreenIllustrations,
    } = this.props;
    onForwardToChangeFullScreenIllustrations(
      sceneCommandForm,
      parentSceneCommandForm?.sceneCommandId,
    );
    onRequestCloseModal();
  };

  private addSound = () => {
    const {
      sceneCommandForm,
      parentSceneCommandForm,
      onForwardToAddSoundEffects,
      onChangeCommand,
      onChangeFullScreenIllustrationShowSceneCommandForm,
    } = this.props;
    onForwardToAddSoundEffects(
      sceneCommandForm,
      parentSceneCommandForm?.sceneCommandId,
      sound => {
        const newSceneCommandForm =
          new FullScreenIllustrationShowSceneCommandForm(
            sceneCommandForm.fullScreenIllustration,
            sound,
            null,
            null,
            sceneCommandForm.sceneCommandId,
          );
        if (parentSceneCommandForm) {
          const newParentSceneCommandForm =
            CompositeSequenceSceneCommandFormFactory.update(
              parentSceneCommandForm,
              newSceneCommandForm,
            );
          onChangeCommand(newParentSceneCommandForm);
        } else {
          onChangeFullScreenIllustrationShowSceneCommandForm(
            newSceneCommandForm,
          );
        }
        this.setState({selectedSound: sound});
      },
    );
  };

  private deleteSound = () => {
    const {
      sceneCommandForm,
      parentSceneCommandForm,
      onChangeFullScreenIllustrationShowSceneCommandForm,
      onChangeCommand,
    } = this.props;
    const newSceneCommandForm = new FullScreenIllustrationShowSceneCommandForm(
      sceneCommandForm.fullScreenIllustration,
      null,
      null,
      null,
      sceneCommandForm.sceneCommandId,
    );
    if (parentSceneCommandForm) {
      const newParentSceneCommandForm =
        CompositeSequenceSceneCommandFormFactory.update(
          parentSceneCommandForm,
          newSceneCommandForm,
        );
      onChangeCommand(newParentSceneCommandForm);
    } else {
      onChangeFullScreenIllustrationShowSceneCommandForm(newSceneCommandForm);
    }
    this.setState({
      selectedSound: null,
      selectedSoundStartTime: null,
      selectedSoundEndTime: null,
    });
  };

  private handleChagenRegion = (start?: number, end?: number) => {
    const {sceneCommandForm, parentSceneCommandForm, onChangeCommand} =
      this.props;
    const newSceneCommandForm = sceneCommandForm.copy(
      sceneCommandForm.sceneCommandId,
    );
    newSceneCommandForm.sound = this.state.selectedSound;
    if (start == undefined && end == undefined) {
      newSceneCommandForm.startTime = null;
      newSceneCommandForm.endTime = null;
    } else {
      newSceneCommandForm.startTime = start === undefined ? null : start;
      newSceneCommandForm.endTime = end === undefined ? null : end;
    }
    this.setState({
      selectedSoundStartTime: start || null,
      selectedSoundEndTime: end || null,
    });
    if (parentSceneCommandForm) {
      const newParentSceneCommandForm =
        CompositeSequenceSceneCommandFormFactory.update(
          parentSceneCommandForm,
          newSceneCommandForm,
        );
      onChangeCommand(newParentSceneCommandForm);
    } else {
      onChangeCommand(newSceneCommandForm);
    }
  };

  private destroySceneCommandForm = () => {
    const {
      sceneCommandForm,
      parentSceneCommandForm,
      onRequestCloseModal,
      onRemoveFullScreenIllustrationShowCommand,
      onChangeCommand,
      onRemoveCommand,
    } = this.props;
    if (parentSceneCommandForm) {
      const newParentSceneCommandForm =
        CompositeSequenceSceneCommandFormFactory.remove(
          parentSceneCommandForm,
          sceneCommandForm,
        );
      if (newParentSceneCommandForm) {
        onChangeCommand(newParentSceneCommandForm);
      } else {
        onRemoveCommand(parentSceneCommandForm);
      }
    } else {
      onRemoveFullScreenIllustrationShowCommand(sceneCommandForm);
    }
    onRequestCloseModal();
  };
}

const styles = StyleSheet.create({
  body: {
    backgroundColor: '#fafafa',
    alignItems: 'center',
    width: '100%',
  } as ViewStyle,
  button: {
    marginVertical: 5,
  } as ViewStyle,
  deleteButtons: {
    marginTop: 5,
  } as ViewStyle,
  footer: {
    padding: 10,
  } as ViewStyle,
});
